/**
 * Live feed hook — fetches recent posts and keeps them updated via SSE.
 *
 * Posts are kept in a sorted array (newest first). SSE events:
 *   post.created → prepend
 *   post.updated → patch matching row (vote tallies, status)
 *   post.deleted → remove
 *
 * Realtime traffic flows through the shared sseHub (services/sseHub.ts) so
 * App.tsx and this hook share a single EventSource. We poll every 15s as a
 * fallback when EventSource is unavailable or while the hub reports offline.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { postsApi, Post, PostCategory } from '../services/postsApi';
import { useSSE } from './useSSE';
import { sseHub } from '../services/sseHub';

export interface UsePostFeedOptions {
  category?: PostCategory | null;
  enabled?: boolean;
}

export function usePostFeed({ category = null, enabled = true }: UsePostFeedOptions = {}) {
  const [posts, setPosts]     = useState<Post[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setLoading(true); setError(null);
    try {
      const r = await postsApi.feed({ category: category || undefined, limit: 100 });
      setPosts(r.posts);
    } catch (e: any) {
      setError(e.message || 'Failed to load feed.');
    } finally {
      setLoading(false);
    }
  }, [category, enabled]);

  // Initial load + on filter change
  useEffect(() => { refresh(); }, [refresh]);

  const matchesFilter = useCallback(
    (p: Post) => !category || p.category === category,
    [category],
  );

  // Realtime: subscribe via the shared hub. These run no-ops when !enabled
  // because the handlers themselves bail out — keeps hook order stable.
  useSSE<Post>('post.created', (post) => {
    if (!enabled || !post) return;
    if (!matchesFilter(post)) return;
    setPosts(prev => prev.some(p => p.id === post.id) ? prev : [post, ...prev].slice(0, 200));
  }, [enabled, category]);

  useSSE<Partial<Post> & { id: number }>('post.updated', (patch) => {
    if (!enabled || !patch) return;
    setPosts(prev => prev.map(p => p.id === patch.id ? { ...p, ...patch } : p));
  }, [enabled]);

  useSSE<{ id: number }>('post.deleted', ({ id }) => {
    if (!enabled || id == null) return;
    setPosts(prev => prev.filter(p => p.id !== id));
  }, [enabled]);

  // Polling fallback — run continuously when EventSource isn't available, or
  // intermittently when the hub is offline. The hub's status flag tells us
  // when the stream is healthy so we can pause polling.
  useEffect(() => {
    if (!enabled) return;
    const noSSE = typeof EventSource === 'undefined';

    const startPoll = () => {
      if (pollRef.current != null) return;
      pollRef.current = window.setInterval(refresh, 15000);
    };
    const stopPoll = () => {
      if (pollRef.current != null) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };

    if (noSSE) {
      startPoll();
      return () => stopPoll();
    }

    const unsub = sseHub.subscribeStatus((online) => {
      if (online) stopPoll();
      else startPoll();
    });
    return () => {
      unsub();
      stopPoll();
    };
  }, [enabled, refresh]);

  // Optimistic helpers — used by PostCard buttons so the UI doesn't wait on
  // the broadcast round-trip.
  const patchLocal = useCallback((id: number, patch: Partial<Post>) => {
    setPosts(prev => prev.map(p => p.id === id ? { ...p, ...patch } : p));
  }, []);
  const removeLocal = useCallback((id: number) => {
    setPosts(prev => prev.filter(p => p.id !== id));
  }, []);
  const prependLocal = useCallback((post: Post) => {
    setPosts(prev => prev.some(p => p.id === post.id) ? prev : [post, ...prev]);
  }, []);

  return { posts, loading, error, refresh, patchLocal, removeLocal, prependLocal };
}
